<template>
  <v-container fluid class="pa-0">

    <!-- Loading template ------------------------------------>
    <centered-loading v-if="loading" :displayBool="true" :small="true" class="mt-10">
    </centered-loading>

    <!-- No elements/fallback template ------------------------>
    <v-container v-else-if="noObjectsFlag && !editMode" fluid class="d-flex flex-column align-center">
      <p class="error--text mt-8">{{ invalidText }}</p>
      <v-btn v-if="newable" @click="createnew()" color="primary">
        <v-icon class="mr-3" size="20">mdi-plus</v-icon>
        {{ $t('servicesystem.common_objects.commonterms.create_new') }}
      </v-btn>
      <p v-else class="error--text mt-4"
        v-text="perssionDeniedText ? perssionDeniedText : $t('servicesystem.common_objects.fallbackterms.create_new_permission_denied')" />
    </v-container>

    <!-- None selected template ------------------------>
    <v-container v-else-if="preselected === null && (index === -2) && !editMode && !selectMode" fluid
      class="d-flex flex-column align-center">
      {{ $t('servicesystem.common_objects.fallbackterms.none_archived') }}
    </v-container>

    <!-- Selectmode template --------------------------------->
    <div v-else-if="selectMode">
      <v-container class="d-flex px-4 pb-2">
        <v-row no-gutters>
          <v-col :cols="0" class="mr-2">
            <v-text-field v-model="listfilter" dense clearable hide-details
              :label="$t('servicesystem.common_objects.commonterms.filter')" />
          </v-col>
          <v-col cols="auto" v-if="newable" class="d-flex justify-end">
            <v-btn @click="createnew()" small color="primary">
              <v-icon size="17" class="mr-1">mdi-plus</v-icon>{{ $t('servicesystem.common_objects.commonterms.new') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
      <QScroll :height="270">
        <v-list-item v-for="item in filteredObjectList" :key="item['Id']" @click="select(item)"
          :class="(item && item.IsActive === false) ? 'deletedcolor' : ''">
          <v-list-item-icon class="my-auto">
            <v-icon v-text="(item && item.IsActive === false) ? editModel.disabledIcon : editModel.enabledIcon"
              :color="(item && item.IsActive === false) ? 'disabled' : ''" />
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title
              v-text="item[editModel.listItemTextKey] + (item.archived ? (' ' + $t('servicesystem.common_objects.commonterms.archived_version')) : '')"
              :class="(item && item.IsActive === false) ? 'useThemeTextColor' : ''" />
          </v-list-item-content>
          <v-list-item-action v-if="editObject && (editObject.Id === item.Id)">
            <v-icon color="primary">mdi-check</v-icon>
          </v-list-item-action>
        </v-list-item>
      </QScroll>
    </div>

    <!-- Editmode/normalmode template -------------------------->
    <div v-else style="position: relative;">
      <overlay-alert v-if="!editMode && !loading" :config="alertconfig"></overlay-alert>
      <v-list class="py-0">
        <div v-for="field in editModel.fields" :key="field.valuekey">
          <address-li v-if="field.type === 'address'" :value="index > -1 ? objectList[index] : field.defaultValue"
            :keyname="field.valuekey" :noempty="field.rules && field.rules.includes('noempty')"
            :edit="editMode && field.editable" @fetch="updateValue">
          </address-li>
          <edit-li v-else-if="priorityShow(field) && field.type === 'input'" :label="field.label"
            :value="index > -1 ? getValueFromKey(field.valuekey) : field.defaultValue" :keyname="field.valuekey"
            :noempty="field.rules && field.rules.includes('noempty')"
            :email="field.rules && field.rules.includes('email')" :tlf="field.rules && field.rules.includes('tlf')"
            :valuecolor="(field.coloredby && getValueFromKey(field.coloredby)) ? 'primary' : ''"
            :edit="editMode && field.editable" @fetch="updateValue">
          </edit-li>
          <switch-li v-else-if="field.type === 'switch'" :label="field.label"
            :value="index > -1 ? getValueFromKey(field.valuekey) : field.defaultValue" :keyname="field.valuekey"
            :edit="editMode && field.editable" :onlyedit="field.editable === 'onlyedit'" @fetch="updateValue">
          </switch-li>
          <area-li v-else-if="priorityShow(field) && field.type === 'area'" :label="field.label"
            :value="index > -1 ? getValueFromKey(field.valuekey) : field.defaultValue" :keyname="field.valuekey"
            :noempty="field.rules && field.rules.includes('noempty')" :edit="editMode && field.editable"
            @fetch="updateValue">
          </area-li>
          <date-li v-else-if="priorityShow(field) && field.type === 'date'" :label="field.label"
            :value="index > -1 ? getValueFromKey(field.valuekey) : field.defaultValue" :keyname="field.valuekey"
            :noempty="field.rules && field.rules.includes('noempty')" :edit="editMode && field.editable"
            @fetch="updateValue">
          </date-li>
        </div>
        <edit-bar :externalTrigger="editMode" :saveable="valid"
          :saveText="index < 0 ? $t('servicesystem.common_objects.commonterms.create') : $t('servicesystem.common_objects.commonterms.save')"
          :dividerPos="'none'" @saveEmit="index < 0 ? add() : change()" @modeEmit="cancel()">
        </edit-bar>
      </v-list>
    </div>
  </v-container>
</template>


<script>
import EditBar from "@/components/Sections/Editbar.vue";
import EditLi from "@/components/Inputs/EditableListItem.vue";
import SwitchLi from "@/components/Inputs/SwitchListItem.vue";
import AddressLi from "@/components/Inputs/AdressListItem.vue";
import AreaLi from "@/components/Inputs/AreaListItem.vue";
import DateLi from "@/components/Inputs/DateListItem.vue";
import CenteredLoading from "@/components/Sections/CenteredLoading.vue";
import OverlayAlert from "@/components/Sections/OverlayAlert.vue";
import QScroll from "@/components/Sections/Containers/QScroll.vue";

export default {
  name: "SelectableEditCard",
  props: [
    "valid",
    "limitId",
    "defaultindex",
    "selectedId",
    "preselected",
    "invalidText",
    "perssionDeniedText",
    "editModel",
    "newable",
    "editMode",
    "selectMode",
  ],
  components: {
    EditBar,
    EditLi,
    SwitchLi,
    AddressLi,
    AreaLi,
    DateLi,
    CenteredLoading,
    OverlayAlert,
    QScroll,
  },

  data() {
    return {
      objectList: [],

      listfilter: "",
      index: null,
      lastValidIndex: 0,
      errorHighLight: false,

      editObject: {},

      prioritySlots: [],

      loading: true,
    };
  },

  mounted() {

    this.getObejctList().then(() => {
      this.parseExternalSelect();
      //this.$emit("fetch", this.editObject);
    });
  },

  methods: {
    parseExternalSelect() {
      var index;
      if (this.preselected === null) {
        this.index = -2;
      } else if (this.preselected) {
        index = this.objectList.findIndex((x) => "" + x.Id === "" + this.preselected.Id);
        if (this.isValidId(index)) {
          //if in list jost set the correct index
          this.index = index;
        } else {
          //else not in list, add preselected to list and set index to it
          this.objectList.unshift(this.preselected);
          this.index = 0;
        }
      } else {
        index = this.objectList.findIndex((x) => "" + x.Id === "" + this.selectedId);
        var defaulti = this.defaultindex ? this.defaultindex : 0;
        this.index = this.isValidId(index) ? index : defaulti;
      }
    },

    priorityShow(field) {
      var returnValue = true;
      if (field.type === "address") {
        //if address no priority can be applied
        return returnValue;
        //else if not in editmode and this field is slotted
      } else if (!this.editMode && field.priorityslot) {
        const indexOfSlot = this.prioritySlots.findIndex((x) => x.slotname === field.priorityslot);
        //this slot is allready processed
        if (indexOfSlot > -1) {
          //if the processed result was this field show
          if (this.prioritySlots[indexOfSlot].fieldname === field.valuekey) {
            returnValue = true;
          }
          //else it was not this field so hide
          else {
            returnValue = false;
          }
        }
        //else slot is not processed do do that
        else {
          var slotPossibleFields = [];
          //find all fields eligable for this slot
          this.editModel.fields.forEach((possibleField) => {
            if (field.priorityslot === possibleField.priorityslot) {
              slotPossibleFields.push(possibleField);
            }
          });
          //find first slot that has value
          var selectedField = false;
          slotPossibleFields.forEach((possibleField) => {
            if (!selectedField) {
              const fieldValue = this.getValueFromKey(possibleField.valuekey);
              if (fieldValue != null && fieldValue != undefined && fieldValue != "") {
                //if field has value
                selectedField = possibleField;
              }
            }
          });
          //if no slots had value just pick the first one
          if (!selectedField) {
            selectedField = slotPossibleFields[0];
          }
          //show or hide based on if this field was the chosen one
          returnValue = selectedField.valuekey === field.valuekey;
          //push the chosen field to array
          this.prioritySlots.push({ slotname: selectedField.priorityslot, fieldname: selectedField.valuekey });
        }
      }
      return returnValue;
    },

    updateValue(object) {
      this.editObject[object.key] = object.value;

    },

    cancel() {
      this.$emit("Normal");
      this.index = this.lastValidIndex;
    },

    select(item) {
      this.index = this.findIndex(item);
      this.$emit("Normal");
    },

    findIndex(item) {
      return this.objectList.findIndex(
        (x) => x[this.editModel.listItemTextKey] === item[this.editModel.listItemTextKey]
      );
    },

    createnew() {
      if (this.index > -1) {
        this.lastValidIndex = this.index;
      }
      this.index = -1;
      this.$emit("Edit");
    },

    createEmpty() {
      this.editObject = {};
      if (this.editModel.ParentIdKey) {
        this.editObject[this.editModel.ParentIdKey] = this.limitId;
      }
    },

    add() {
      this.loading = true;
      this.editObject.Id = null;
      var dto = Object.assign({}, this.editObject);

      this.editModel.methods
        .create(dto)
        .then((response) => {
          this.objectList.push(response);
          this.index = this.findIndex(response);
          this.$emit("Normal");
        })
        .catch((exception) => console.log(exception))
        .finally(() => {
          this.loading = false;
        });
    },

    change() {
      this.loading = true;
      this.editModel.methods
        .save(this.editObject)
        .then((response) => {
          this.objectList[this.index] = response;
          this.$emit("Normal");
        })
        .catch((/*exception*/) => {

        })
        .finally(() => {
          this.loading = false;
        });
      //TODO: add visual loading
    },

    async getObejctList() {
      this.loading = true;
      await this.editModel.methods
        .getlist(this.limitId)
        .then((response) => {
          this.objectList = response;

          //if preselected is specifically set to null (not undefined) then clear editobject and index
          if (this.preselected === null) {
            this.index = -2;
            this.editObject = null;
          } else if (this.preselected) {
            //IF preselected and its id is not in list unshift it
            var indexinlist = this.objectList.findIndex((x) => x["Id"] === this.preselected["Id"]);
            if (indexinlist < 0) {
              this.objectList.unshift(this.preselected);
              this.index = 0;
            }
          } else if (this.editObject["Id"] === null || this.editObject["Id"] === undefined) {
            //else if no preselected and editobject has id
            this.index = this.defaultindex ? this.defaultindex : this.index;

            if (!this.noObjectsFlag) {
              this.editObject = Object.assign({}, this.objectList[this.index]);

            } else {
              this.createEmpty();
            }
          }
        })
        .catch((exception) => console.log(exception))
        .finally(() => (this.loading = false));
    },

    getValueFromKey(keychain) {
      var object = this.objectList[this.index];
      const keys = keychain.split(".");
      var returnValue = keys.reduce(function (reducedObject, potentialKey) {
        const keyValue = reducedObject ? reducedObject[potentialKey] : undefined;
        return keyValue;
      }, object || self);
      return returnValue;
    },
  },

  computed: {
    filteredObjectList() {
      return this.objectList.filter((v) =>
        v && v[this.editModel.listItemTextKey]
          ? v[this.editModel.listItemTextKey].toUpperCase().includes(this.listfilter.toUpperCase())
          : null
      );
    },
    //TODO: legg inn haandtering av name allready taken response
    noObjectsFlag() {
      return !this.loading && (!this.objectList || !this.objectList[0]);
    },

    alertconfig() {
      if (this.objectList) {
        if (this.editObject.IsActive === false) {
          return {
            color: "quinary",
            icon: "mdi-alert-circle-outline",
            text: "servicesystem.common_objects.commonterms.deactivated",
            small: true,
          };
        }
      }
      return false;
    },
  },

  watch: {
    editMode: function () {
      if (this.noObjectsFlag) {
        if (!this.editMode) {
          this.$emit("NoObjects");
        }
      } else {
        if (this.index > -1) {
          this.lastValidIndex = this.index; //set fallback if not new
        }
        this.editObject = Object.assign({}, this.objectList[this.index]);
      }
    },

    index: function () {

      if (this.index > -1) {
        this.editObject = Object.assign({}, this.objectList[this.index]);
        this.$emit("fetch", this.editObject);
      } else if (this.index === -1) {
        this.createEmpty();
      }
    },

    noObjectsFlag: function (newval) {
      if (newval) {
        this.$emit("NoObjects");
      } else {
        this.$emit("Normal");
      }
    },

    selectedId: function () {

      if (!this.loading) {
        this.parseExternalSelect();
      }
    },

    preselected: function () {
      if (!this.loading) {
        this.parseExternalSelect();
      }
    },

    limitId: function () {
      this.getObejctList().then(() => {
        this.parseExternalSelect();
      });
    },

    editObject: function () {

    },
  },
};
</script>

<style lang="scss" scoped>
/*
.limito {
  height: calc(100% - 50px);
  overflow-y: auto;
}
*/
</style>